import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import styled, { createGlobalStyle, css } from "styled-components";

export const DetailsLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 24px;
  min-height: 0;
  padding: 24px 0 32px;
`;

export const SectionTitle = styled.h2`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 18px;
  font-weight: 600;
  line-height: 26px;
`;

export const SelectorPanel = styled.section`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  gap: 16px;
  min-height: 0;
  min-width: 0;
`;

export const TabsList = styled.div`
  display: flex;
  flex-wrap: nowrap;
  border-bottom: 1px solid #e7e7e7;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none;
  background: #ffffff;

  &::-webkit-scrollbar {
    display: none;
  }
`;

export const TabButton = styled.button<{
  $isActive?: boolean;
  $isLocked?: boolean;
}>`
  height: 36px;
  padding: 0 12px;
  border: none;
  border-right: 1px solid white;
  border-bottom: 4px solid transparent;
  background: ${({ theme }) => theme.sidebarHeader.background2};
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  font-style: normal;
  line-height: 20px;
  text-align: center;
  letter-spacing: 0;
  white-space: nowrap;
  flex: 0 0 auto;
  cursor: pointer;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;

  &:hover {
    background: ${({ theme }) => theme.sidebarHeader.background2};
    color: #005fb8;
  }

  ${(props) =>
    props.$isActive &&
    css`
      border-bottom-color: ${props.theme.buttons.style1.bg};
      background: ${props.theme.sidebarHeader.background1};
      color: ${props.theme.buttons.style1.bg};
    `}

  ${(props) =>
    props.$isLocked &&
    css`
      color: #9fa0a4;
      background: ${props.theme.sidebarHeader.background2};
      cursor: not-allowed;

      &:hover {
        background: ${props.theme.sidebarHeader.background2};
        color: #9fa0a4;
      }
    `}

  &:first-child {
    border-left: 1px solid white;
  }
`;

export const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 100%;
`;

export const LockedBadge = styled.span`
  font-size: 12px;
  line-height: 18px;
  color: #7c7f7e;
`;

export const Toolbar = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
  padding: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
  background: #ffffff;
`;

export const SearchField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 320px;
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;

  @media (max-width: 768px) {
    min-width: 100%;
  }
`;

export const SearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 38px;
  padding: 12px 52px 12px 20px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
  background-color: ${({ theme }) => theme.searchPatients.input.bg};
  color: ${({ theme }) => theme.searchPatients.input.text};
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;

  &:focus {
    outline: none;
  }
`;

export const SearchInputWrapper = styled.form`
  position: relative;
  max-width: 357px;
  flex: 1 1 320px;
`;

export const SearchInputIcon = styled.button`
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #7c7f7e;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
`;

export const TableMetaGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 40px;
  flex-wrap: wrap;
`;

export const RowsControl = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 16px;
`;

export const RowsPerPageLabel = styled.span`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

export const TableWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

export const Table = styled(DataTable)`
  & thead tr th {
    padding: 15px 12px;
    background: white;
    color: #181818;
    border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
    border-width: 0 0 1px 0;

    & .p-sortable-column-icon,
    & .table-sort-icon {
      display: inline-flex;
      align-items: center;
      margin-left: 10px;
      vertical-align: middle;
    }
  }

  & tbody tr td {
    padding: 12px;
    background: white;
    color: #181818;
    border: 1px solid ${({ theme }) => theme.searchPatients.controls.tabOutline};
    border-width: 0 0 1px 0;
  }

  & .p-datatable-tbody > tr {
    cursor: pointer;
    transition: background-color 0.2s ease;
  }

  & .p-datatable-tbody > tr:hover > td {
    background: #f8f8f8;
  }

  &.is-readonly .p-datatable-tbody > tr {
    cursor: default;
  }

  &.is-readonly .p-datatable-tbody > tr:hover > td {
    background: #f3f4f6;
  }
`;

export const CheckboxCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const CheckboxInput = styled.input`
  appearance: none;
  width: 16px;
  height: 16px;
  cursor: pointer;
  border: 1.5px solid #9fa0a4;
  border-radius: 4px;
  background: #ffffff;

  &:checked {
    border-color: ${({ theme }) => theme.buttons.style1.bg};
    background-color: ${({ theme }) => theme.buttons.style1.bg};
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='8' viewBox='0 0 10 8' fill='none'%3E%3Cpath d='M1 4L3.5 6.5L9 1' stroke='white' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: center;
    background-size: 10px 8px;
  }
`;

export const PaginatorWrapper = styled.div`
  position: relative;
  width: 390px;
  min-height: 46px;
  flex: 0 0 390px;

  .p-paginator-pages button {
    font-size: 14px;
    position: relative;
    top: -2px;
  }

  .p-paginator > button {
    padding: 4px;
  }

  @media (max-width: 768px) {
    width: 100%;
    flex: 1 1 100%;
    overflow-x: auto;
  }
`;

export const PageReportTitle = styled.span`
  color: #181818;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 24px;
`;

export const RowsPerPage = styled(Dropdown)`
  min-width: 96px;

  &.p-dropdown {
    height: 38px;
    border: 1px solid #e7e7e7;
    border-radius: 10px;
    background: #ffffff;
    transition:
      border-color 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      border-color: #d6d6d6;
    }

    &.p-focus,
    &:focus-within {
      border-color: #cfcfcf;
      box-shadow: 0 0 0 3px rgba(24, 24, 24, 0.04);
    }
  }

  .p-dropdown-label {
    display: flex;
    align-items: center;
    height: 100%;
    padding: 0 0 0 16px;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
    color: #181818;
  }

  .p-dropdown-trigger {
    width: 40px;
    color: #8b8b8b;
  }

  .p-dropdown-trigger-icon {
    font-size: 16px;
  }
`;

export const RowsPerPagePanelStyles = createGlobalStyle`
  .roi-request-rows-per-page-panel {
    margin-top: 6px;
    border: 1px solid #e7e7e7;
    border-radius: 10px;
    background: #ffffff;
    box-shadow:
      0 12px 24px rgba(16, 24, 40, 0.08),
      0 2px 6px rgba(16, 24, 40, 0.04);
    overflow: hidden;
  }

  .roi-request-rows-per-page-panel .p-dropdown-items {
    padding: 6px;
  }

  .roi-request-rows-per-page-panel .p-dropdown-item {
    min-height: 40px;
    display: flex;
    align-items: center;
    padding: 8px 12px;
    border-radius: 8px;
    font-family: Inter, sans-serif;
    font-size: 16px;
    line-height: 24px;
    color: #181818;
  }

  .roi-request-rows-per-page-panel .p-dropdown-item.p-highlight {
    background: #f5f5f5;
    color: #181818;
  }

  .roi-request-rows-per-page-panel
    .p-dropdown-item:not(.p-highlight):not(.p-disabled):hover {
    background: #f8f8f8;
  }
`;

export const EmptyState = styled.div`
  min-height: 180px;
  border: 1px dashed ${({ theme }) => theme.searchPatients.controls.tabOutline};
  border-radius: 10px;
  background: #fafafa;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  text-align: center;
  padding: 24px;
`;

export const SearchEmptyState = styled.div`
  flex: 1 1 auto;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 16px 0;
`;

export const SearchEmptyIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;

  & > svg {
    width: 40px;
    height: 40px;
  }
`;

export const SearchEmptyTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

export const SearchEmptyText = styled.div`
  color: #3f3f3f;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  width: min(100%, 340px);
  margin: 0;
`;

export const EmptyStateText = styled.p`
  color: #5a5959;
  font-family: Inter, sans-serif;
  font-size: 14px;
  line-height: 20px;
  max-width: 420px;
`;
